import { useCallback, useEffect, useState } from "react";
import { Chain } from "viem";
import {
  Button,
  Flex,
  Text,
  Box,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  BoxProps,
  useDisclosure,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerBody,
} from "@chakra-ui/react";

// react-icons
import { FiMenu } from "react-icons/fi";
import { AiOutlineMore } from "react-icons/ai";
import { useDispatch } from "react-redux";
import { useAccount, useSwitchChain, useWalletClient } from "wagmi";

import { updateModalData } from "store";

import { MODAL_TYPES } from "@constants/modal";
import { CONNECT_WALLET_CLICK, NETWORK_SWITCHED } from "@constants/analytics";

import { chainIconMapping, trackEvent } from "utils";

import WalletInfo from "../WalletInfo";
import SubHeader from "./SubHeader";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import { FaChevronDown } from "react-icons/fa6";
import { useRouter } from "next/router";
import useDappConfig from "@hooks/useDappConfig";
import useLedgerLive from "@hooks/useLedgerLive";
import UTMLink from "@components/UTMLink";
import BaseImage from "@components/BaseImage";
import { updateSelectedChain } from "store/slices/globalSlice";
import { CONNECT_WALLET_CTA } from "@constants/common";
import Sidebar from "@components/Sidebar";
import useBanner from "@hooks/useBanner";
import { IoWarning } from "react-icons/io5";
import { arbitrum, mainnet } from "viem/chains";

interface HeaderProps extends BoxProps {
  hideWalletButton?: boolean;
  hideChainDropdown?: boolean;
  showRestakeButton?: boolean;
  onlySubMenu?: boolean;
  secondaryVariant?: boolean;
  kelplogo?: boolean;
}

const Header = ({
  hideWalletButton,
  hideChainDropdown,
  showRestakeButton = false,
  onlySubMenu = false,
  secondaryVariant = false,
  kelplogo = false,
  ...rest
}: HeaderProps) => {
  const router = useRouter();
  const {
    isConnected,
    address,
    connector: activeConnector,
    chain,
  } = useAccount();
  const { data: signer } = useWalletClient();
  const { openConnectModal } = useConnectModal();

  const { showBanner } = useBanner();
  const {
    restakeEnabledChains,
    claimEnabledChains,
    vaultEnabledChains,
    swapEnabledChains,
    yieldVaultEnabledChains,
  } = useDappConfig();
  const { switchChain } = useSwitchChain();
  const { isLedgerConnected } = useLedgerLive();

  const { isOpen, onOpen, onClose } = useDisclosure();

  const pathname = router.pathname;

  const isRestake = pathname.includes("restake");
  const isAirdropVault = pathname.includes("airdrop-gain");
  const isYieldVault = pathname.includes("growth-vault");
  const isWrapAgETH = pathname.includes("bridge-wrap-ageth");
  const isWrap = pathname.includes("bridge-wrap");

  let chainList;

  if (isRestake) {
    chainList = restakeEnabledChains;
  } else if (isAirdropVault) {
    chainList = vaultEnabledChains;
  } else if (isYieldVault) {
    chainList = yieldVaultEnabledChains;
  } else if (isWrapAgETH) {
    chainList = vaultEnabledChains;
  } else if (isWrap) {
    chainList = [mainnet, arbitrum, ...swapEnabledChains];
  } else {
    chainList = claimEnabledChains;
  }

  // const chainList =
  //   pathname.includes("restake") || pathname.includes("wrap-bridge")
  //     ? restakeEnabledChains
  //     : pathname.includes("gain") || pathname.includes("wrap-bridge-ageth")
  //     ? vaultEnabledChains
  //     : pathname.includes("wrap-agETH")
  //     ? vaultSwapEnabledChains
  //     : pathname.includes("wrap")
  //     ? swapEnabledChains
  //     : claimEnabledChains;

  const chainListIds = chainList.map((chain) => chain.id);

  const handleSwitchChain = (updatedChain: Chain) => {
    const prevChain = chain;
    switchChain(
      { chainId: updatedChain.id },
      {
        onError: (error) => {
          console.log(error.message);

          if (error.message.includes("Chain not configured")) {
            signer
              ?.addChain({ chain: updatedChain })
              .then(() => switchChain({ chainId: updatedChain.id }));
          }
          return;
        },
        onSuccess: () => {
          trackEvent(NETWORK_SWITCHED, {
            prev_chain_id: prevChain?.id,
            prev_chain: prevChain?.name,
          });
        },
      }
    );
  };

  const dispatch = useDispatch();
  const [isSubMenuVisible, setIsSubMenuVisible] = useState(false);

  const handleConnectWallet = useCallback(() => {
    openConnectModal?.();
    trackEvent(CONNECT_WALLET_CLICK, {
      cta_location: "header",
      cta: CONNECT_WALLET_CTA,
    });
  }, []);

  const handleWalletClick = useCallback(() => {
    dispatch(
      updateModalData({
        modalType: MODAL_TYPES.WALLET_MODAL,
      })
    );
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      updateSelectedChain({
        name: chain?.name,
        id: chain?.id,
      })
    );
  }, [chain, dispatch]);

  const renderSubMenu = () => {
    return (
      <>
        <IconButton
          variant="icon"
          alignItems="center"
          icon={<AiOutlineMore height="24px" width="24px" />}
          onClick={() => setIsSubMenuVisible(true)}
          aria-label="change theme"
          ml="4px"
          borderRadius="0.5rem"
          display={{ base: "flex", lg: "none" }}
          bg="backgroundCard"
          minW="32px"
          h="32px"
        />
        <SubHeader
          shouldDisplayMenu={isSubMenuVisible}
          onChangeDisplayMenu={(isVisible) => setIsSubMenuVisible(isVisible)}
        />
      </>
    );
  };

  return (
    <Flex
      gap="16px"
      alignItems="center"
      justifyContent={{ base: "space-between", xl: "flex-end" }}
      position={{ xl: "sticky" }}
      right="0px"
      top="0"
      zIndex="2"
      p="1.5rem"
      pt={{
        base: showBanner ? "2em" : "1.5rem",
        md: showBanner ? "1rem" : "1.5rem",
      }}
      backgroundColor={secondaryVariant ? "#075A5A" : "backgroundCanvas"}
      {...rest}
    >
      {kelplogo && (
        <Flex
          display={{ base: "none", xl: "inherit" }}
          position={"absolute"}
          left="30px"
          zIndex={2}
        >
          <UTMLink href="/">
            <BaseImage
              src={"/assets/kelp_white.svg"}
              alt="kelp"
              height={40}
              width={106}
              loading="eager"
            />
          </UTMLink>
        </Flex>
      )}
      <Box display={{ base: "inherit", xl: "none" }} alignItems="center">
        <IconButton
          display={{ base: "flex", xl: "none" }}
          onClick={onOpen}
          aria-label="open menu"
          h="24px"
          w="24x"
          bg="transperent"
          color="textPrimary"
          variant="unstyled"
          icon={
            <FiMenu
              height="24px"
              width="24px"
              style={secondaryVariant ? { color: "#FFF" } : {}}
            />
          }
        />
        <Box pl="1rem">
          <BaseImage
            src={
              secondaryVariant
                ? "/assets/header/kelp-icon-white.svg"
                : "/assets/header/kelp-icon.svg"
            }
            alt="kelp"
            height={24}
            width={19}
            loading="eager"
          />
        </Box>
      </Box>

      {!hideWalletButton && (
        <Flex>
          {!isConnected && !activeConnector ? (
            <Button
              borderRadius={{ base: "6px", lg: "8px" }}
              p={{ base: "4px 45px", md: "16px 64px" }}
              bg="backgroundCard"
              h={{ base: "32px", lg: "56px" }}
              _hover={{
                bg: "backgroundCard",
              }}
              w={{ base: "120px", lg: "240px" }}
              onClick={handleConnectWallet}
            >
              <Text
                fontWeight={{ base: "700", lg: "600" }}
                fontSize={{ base: "10px", lg: "14px" }}
                lineHeight={{ base: "16px", lg: "24px" }}
                color="textPrimary"
              >
                {CONNECT_WALLET_CTA}
              </Text>
            </Button>
          ) : (
            <Flex alignItems="center" gap={4}>
              {!hideChainDropdown && !isLedgerConnected && (
                <Box pos="relative">
                  <Menu>
                    <MenuButton
                      as={Button}
                      h={{ base: "32px", lg: "56px" }}
                      rightIcon={
                        chainListIds.length > 1 || !chain ? (
                          <FaChevronDown />
                        ) : (
                          <></>
                        )
                      }
                      w="100%"
                      textAlign="left"
                      className="menuButtonStake"
                      p={{ base: "16px", md: "16px 16px 16px 24px" }}
                      _active={{
                        bg: "backgroundCard",
                      }}
                      _hover={{
                        bg: "backgroundCard",
                      }}
                      borderRadius="8px"
                      bg="backgroundCard"
                      color="textPrimary"
                      fontWeight="400"
                      fontSize={{ base: "12px", md: "14px" }}
                    >
                      <Flex alignItems="center" gap="8px">
                        {chain && chainListIds.includes(chain.id) ? (
                          <>
                            <Box
                              overflow="hidden"
                              pos="relative"
                              borderRadius="50%"
                            >
                              <BaseImage
                                height={24}
                                width={24}
                                src={(chain as any).iconUrl}
                                alt={chain!.name}
                              />
                            </Box>
                            <Flex
                              alignItems="center"
                              justify="space-between"
                              flex={1}
                              gap={2}
                              display={{ base: "none", lg: "block" }}
                            >
                              <Box flex={1}>{chain.name}</Box>
                            </Flex>
                          </>
                        ) : (
                          <Flex
                            alignItems="center"
                            justify="space-between"
                            flex={1}
                            gap={2}
                          >
                            <Box
                              display={{ base: "none", md: "block" }}
                              flex={{ md: 1 }}
                            >
                              Unsupported Chain
                            </Box>
                            <Box display={{ base: "block", md: "none" }}>
                              <IoWarning color="#F56565" />
                            </Box>
                          </Flex>
                        )}
                      </Flex>
                    </MenuButton>
                    <MenuList
                      transformOrigin="top !important"
                      w={{
                        base: "180px",
                        sm: "100%",
                      }}
                      p="8px"
                      border="1px solid #AEDFCA"
                      bg="backgroundCard"
                      fontSize="14px"
                      fontWeight="400"
                    >
                      {chainList.map((option) => {
                        return (
                          <MenuItem
                            key={option.id}
                            padding={2}
                            marginBottom={1}
                            onClick={() => {
                              handleSwitchChain(option);
                            }}
                          >
                            <Flex
                              justifyContent="space-between"
                              w="100%"
                              alignItems="center"
                            >
                              <Flex alignItems="center" gap="8px" flex={1}>
                                <Box
                                  overflow="hidden"
                                  pos="relative"
                                  borderRadius="50%"
                                >
                                  <BaseImage
                                    height={24}
                                    width={24}
                                    src={chainIconMapping[option.id]}
                                    alt={option!.name}
                                  />
                                </Box>
                                <Flex
                                  justify="space-between"
                                  flex={1}
                                  alignItems="center"
                                  gap={2}
                                >
                                  <Box flex={1} fontSize={14}>
                                    {option.name}
                                  </Box>
                                </Flex>
                              </Flex>
                            </Flex>
                          </MenuItem>
                        );
                      })}
                    </MenuList>
                  </Menu>
                </Box>
              )}
              <WalletInfo
                walletAddress={address}
                activeConnector={activeConnector!}
                onClick={handleWalletClick}
              />
            </Flex>
          )}

          {renderSubMenu()}
        </Flex>
      )}

      {showRestakeButton && (
        <Flex>
          <UTMLink href="/restake">
            <Button
              borderRadius={{ base: "6px", lg: "8px" }}
              p={{ base: "4px 45px", md: "16px 64px" }}
              bg="backgroundCard"
              h={{ base: "32px", lg: "56px" }}
              _hover={{
                bg: "backgroundCard",
              }}
              w={{ base: "120px", lg: "240px" }}
            >
              <Text
                fontWeight={{ base: "700", lg: "600" }}
                fontSize={{ base: "10px", lg: "14px" }}
                lineHeight={{ base: "16px", lg: "24px" }}
                color="textPrimary"
              >
                Restake Now
              </Text>
            </Button>
          </UTMLink>
          {renderSubMenu()}
        </Flex>
      )}

      <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerBody>
            <Sidebar />
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {onlySubMenu && !showRestakeButton && hideWalletButton && (
        <Flex>{renderSubMenu()}</Flex>
      )}
    </Flex>
  );
};

export default Header;